import {
    compileCloudLoggingFilter,
    type CloudLoggingQueryRequest,
    type GuidedCloudLoggingQueryRequest,
} from '@pillar-portal/api-types';

export type GuidedCloudLoggingDraft = Omit<GuidedCloudLoggingQueryRequest, 'limit' | 'cursor'>;

export interface CloudLoggingQueryState {
    readonly mode: CloudLoggingQueryRequest['mode'];
    readonly guided: GuidedCloudLoggingDraft;
    readonly rawDraft: string;
    readonly rawDraftSeeded: boolean;
}

export function createDefaultCloudLoggingQueryState(now: Date): CloudLoggingQueryState {
    const endTimestamp: string = now.toISOString();
    const startTimestamp: string = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
    return {
        mode: 'guided',
        guided: {
            mode: 'guided',
            start_timestamp: startTimestamp,
            end_timestamp: endTimestamp,
        },
        rawDraft: '',
        rawDraftSeeded: false,
    };
}

export function switchCloudLoggingQueryMode(
    state: CloudLoggingQueryState,
    mode: CloudLoggingQueryRequest['mode']
): CloudLoggingQueryState {
    if (mode === state.mode) {
        return state;
    }
    if (mode === 'raw' && !state.rawDraftSeeded) {
        return {
            ...state,
            mode,
            rawDraft: compileCloudLoggingFilter(state.guided),
            rawDraftSeeded: true,
        };
    }
    return { ...state, mode };
}

export function updateGuidedCloudLoggingDraft(
    state: CloudLoggingQueryState,
    update: (current: GuidedCloudLoggingDraft) => GuidedCloudLoggingDraft
): CloudLoggingQueryState {
    const guided: GuidedCloudLoggingDraft = update(state.guided);
    return {
        ...state,
        guided,
        rawDraft: compileCloudLoggingFilter(guided),
        rawDraftSeeded: true,
    };
}

export function updateRawCloudLoggingDraft(
    state: CloudLoggingQueryState,
    rawDraft: string
): CloudLoggingQueryState {
    return { ...state, rawDraft, rawDraftSeeded: true };
}

export function buildActiveCloudLoggingQuery(
    state: CloudLoggingQueryState
): CloudLoggingQueryRequest {
    return state.mode === 'guided' ? state.guided : { mode: 'raw', filter: state.rawDraft };
}

export function formatCloudLoggingTimestampInput(timestamp: string): string {
    const date = new Date(timestamp);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return localDate.toISOString().slice(0, 16);
}

export function parseCloudLoggingTimestampInput(value: string): string | null {
    const timestamp = new Date(value);
    return Number.isNaN(timestamp.getTime()) ? null : timestamp.toISOString();
}
